import {createStateMaker, values} from '@shared/helpers/object'
import {qs} from '@shared/helpers/queryString'

import {isAlphaProfile, isDevProfile, isLocalProfile} from '@web/core/config'
import {APIStatus, ClosingTiming, OrderChannel, PageSet} from '@web/core/constants'
import {PAYMENT_ROUTES, PAYMENT_METHODS_ROUTES, ROUTER_ORDER_COUPON} from '@web/core/routes'
import {LanguageType} from '@web/i18n'

import {DiconEntry, Entry, PartnersEntry} from './constants'
import {CommonState, EntryType, OrderEntryType} from './interfaces'

const EasyBookings = [OrderChannel.EASY_BOOKING, OrderChannel.LOCAL_PAY, OrderChannel.CAFE, OrderChannel.BLOG].join('|')

const PathnameRegexp = {
    ORDER_SHEET_APP: /^\/(order|ordersheet)\//,
    ORDER: new RegExp(`/order/checkout(?:/(${values(OrderChannel).join('|')}))?/`),
    ORDER_SHEET_CHECKOUT: new RegExp(`/ordersheet(?:/(${values(OrderChannel).join('|')}))?/`),
    MALL_CHECKOUT: /\/ordersheet\/mall\//,
    ORDER_MALL_CHECKOUT: /\/order\/(checkout|apply|fail)\/mall\//,
    FLEA_MARKET_CHECKOUT: /\/ordersheet\/fleamarket/,
    MALL_BRIDGE_CHECKOUT: /^\/order\/bridge\/mall(\/.*)?$/,
    EASY_BOOKING_BRIDGE_CHECKOUT: new RegExp(`/order/bridge(?:/(${EasyBookings}))?/`),
    EASY_BOOKING_CHECKOUT: new RegExp(`/ordersheet/(?:/(${EasyBookings}))?/`),
    ORDER_EASY_BOOKING_CHECKOUT: new RegExp(`/order/(checkout|apply|fail)(?:/(${EasyBookings}))?/`),
    FAMILY_PAY_CHECKOUT: /\/ordersheet\/familyPay(?:\/(?!result))/,
    ORDER_RESULT: new RegExp(`/order/result(?:/(${values(OrderChannel).join('|')}))?/`),
    MALL_RESULT: new RegExp(`/order/result/${OrderChannel.MALL}?/`),
    EASY_BOOKING_RESULT: /\/order\/result\/easybooking\//,
    LOCAL_PAY_RESULT: /\/order\/result\/localpay\//,
    BLOG_RESULT: /\/order\/result\/blog\//,
    FLEA_MARKET_RESULT: /\/order\/result\/fleamarket\//,
    SELLER_RESULT: new RegExp(`/order/result/${OrderChannel.SELLER}?/`),
    BOOKING: /\/order\/paymentMethod\/booking?/,
    SUBSCRIPTION: /\/ordersheet\/subscription\/?/,
    /** TODO(패밀리결제): coupon도 pageSet으로 분리 */
    SUBSCRIPTION_MANAGEMENT: /\/order\/coupon\/subscription?/,
    /*
    /**
     * C2 주문서는 /ordersheet/seller 뒤에 추가 path가 붙는 경우 모두 orderSheetId가 아니므로 제외 추가처리 했습니다.
     * path가 추가될때마다 이 정규식이 변경되어야하므로 추후 개선과제로 개선 진행하면 좋을 것 같습니다.
     */
    SELLER: new RegExp(`/ordersheet/seller(?:/(?!${['fail', 'result', 'apply', 'popup', 'fds'].join('|')}))`),
    SELLER_CHECKOUT: /\/ordersheet\/seller(?:\/(?!result))/,
    ORDER_SHEET_SELLER_RESULT: /\/ordersheet\/seller\/result\/?/,
    DICON_BUYING: /\/digitalContents\/buy\/checkout\//,
    DICON_SUBSCRIBE: /\/paymentMethods\/digitalcontents\/(?!(result|bridge))/,
    PARTNERS: /\/instantPay\/partners\//,
    PARTNERS_AUTO: /\/paymentMethods\/partners\//,
    DELIVERY_MANAGER: /\/order\/delivery\/(?!tracking)/,
    DELIVERY_MANAGER_CHANGE: /\/order\/delivery\/(orderResult|scheduledOrderResult)\//,
    ORDER_DETAIL: /\/order\/status\//,
    PURCHASE_CONFIRMATION: /\/order\/purchaseDecision\//,
    // eslint-disable-next-line prefer-regex-literals
    DETAIL: new RegExp(`/order/status(?:/(popupBridge))?/`),
    RESULT: new RegExp(`/order/result(?:/(${Object.values(OrderChannel).join('|')}))?/|/ordersheet/familyPay/result/`),
    PAYMENT_DETAIL: /\/instantPay\/detail\//,
    RECEIVE_POINT: /\/instantPay\/receivePoint\//,
    /** 결제수단 변경창 Page path */
    PAYMENT_METHOD_MANAGEMENT: /\/order\/paymentMethod\/?/,
    SALE_HISTORY: /^\/order\/sale\/history(\/)?$/,
    SALE_HISTORY_DETAIL: /^\/order\/sale\/history\/\d+$/,
    SALE_SETTLEMENT: /^\/order\/sale\/settlement(\/)?$/,
    SALE_SETTLEMENT_DETAIL: /^\/order\/sale\/settlement\/\d+$/,
    NPAY_BIZ_PAYMENT_CHECKOUT: /^\/payments\/checkout?/,
    INVOICE: /\/payments\/invoice\//,
}

/**
 * 경로명에서 진입점(Entry)을 추출합니다.
 * @param pathname - 분석할 경로명 (기본값: window.location.pathname)
 * @returns 진입점 상수 또는 null
 */
export const getEntryFromPathname = (pathname: string = window.location.pathname) => {
    if (PathnameRegexp.INVOICE.test(pathname)) {
        return Entry.INVOICE
    }

    return null
}

/**
 * 주문서 URL을 생성합니다.
 * @param location - Location 객체 (기본값: window.location)
 * @returns 주문서 URL 문자열
 */
export const getOrderSheetUrl = (location: Location = window.location) => {
    return location.toString()
}

/**
 * 경로명을 분석하여 다양한 페이지 타입을 확인하는 함수들을 반환합니다.
 * @param pathname - 분석할 경로명
 * @returns 각 페이지 타입을 확인하는 boolean 함수들의 객체
 */
const getPathNameInfo = (
    pathname: string,
): Record<
    | 'isOrderPathname'
    | 'isSellerPathname'
    | 'isDiconBuyingPathname'
    | 'isDiconAutoPathname'
    | 'isPartnersAutoPathname'
    | 'isOrderSheetPathname'
    | 'isOrderSheetCheckoutPathname'
    | 'isPaymentMethodsPathname'
    | 'isDetailPathName'
    | 'isResultPathName'
    | 'isDeliveryManagerPathName'
    | 'isMallCheckout'
    | 'isEasyBookCheckout'
    | 'isSellerCheckout'
    | 'isFamilyPayCheckout'
    | 'isOrderResult'
    | 'isMallResult'
    | 'isEasyBookResult'
    | 'isSellerResult'
    | 'isOrderSheetSellerResult'
    | 'isOrderSheetApp'
    | 'isOrderDetail'
    | 'isPurchaseConfirmation'
    | 'isDiconAuto'
    | 'isPartnersAuto'
    | 'isPartnersPathname'
    | 'isPaymentDetail'
    | 'isReceivePoint'
    | 'isShoppingSubscription'
    | 'isNaverFinancialAuto'
    | 'isNaverFinancialPayment'
    | 'isCoupon'
    | 'isDeliveryManagerChange'
    | 'isSubscription'
    | 'isSubscriptionManagement'
    | 'isSaleHistory'
    | 'isSaleHistoryDetail'
    | 'isSaleSettlement'
    | 'isSaleSettlementDetail'
    | 'isPaymentMethodManagement'
    | 'isLocalPayResult'
    | 'isBlogResult'
    | 'isNpayBizPaymentCheckout'
    | 'isFleaMarketResult'
    | 'isInvoice',
    () => boolean
> => {
    const isOrderPathname = () => PathnameRegexp.ORDER.test(pathname)
    const isSellerPathname = () => PathnameRegexp.SELLER.test(pathname)
    const isDiconBuyingPathname = () => PathnameRegexp.DICON_BUYING.test(pathname)
    const isDiconAutoPathname = () => PathnameRegexp.DICON_SUBSCRIBE.test(pathname)
    const isPartnersPathname = () => PathnameRegexp.PARTNERS.test(pathname)
    const isPartnersAutoPathname = () => PathnameRegexp.PARTNERS_AUTO.test(pathname)
    const isOrderSheetCheckoutPathname = () => PathnameRegexp.ORDER_SHEET_CHECKOUT.test(pathname)
    const isPaymentDetail = () => PathnameRegexp.PAYMENT_DETAIL.test(pathname)
    /** 정기구독 신청 flow */
    const isSubscription = () => PathnameRegexp.SUBSCRIPTION.test(pathname)
    /** 정기구독 결제수단 변경 */
    const isSubscriptionManagement = () => PathnameRegexp.SUBSCRIPTION_MANAGEMENT.test(pathname)
    /**
     * 정기구독 신청 + 결제수단 변경 모두 pageset이 PageSet.Subscription
     * 패밀리결제 2차 작업을 통해 결제수단 변경창은 pageset이 paymentMethodManagement로 변경될 예정
     */
    const isShoppingSubscription = () => {
        return isSubscription() || isSubscriptionManagement()
    }

    const isNpayBizPaymentCheckout = () => PathnameRegexp.NPAY_BIZ_PAYMENT_CHECKOUT.test(pathname)

    const isInvoice = () => PathnameRegexp.INVOICE.test(pathname)

    return {
        isOrderPathname,
        isSellerPathname,
        isDiconBuyingPathname,
        isDiconAutoPathname,
        isPartnersAutoPathname,
        isPartnersPathname,
        isOrderSheetCheckoutPathname,
        isOrderSheetPathname: () => isOrderPathname() || isSellerPathname() || isOrderSheetCheckoutPathname(),
        isPaymentMethodsPathname: () => isDiconAutoPathname() || isPartnersAutoPathname(),
        isDetailPathName: () => PathnameRegexp.DETAIL.test(pathname),
        isResultPathName: () => PathnameRegexp.RESULT.test(pathname),
        isDeliveryManagerPathName: () => PathnameRegexp.DELIVERY_MANAGER.test(pathname),
        isMallCheckout: () =>
            PathnameRegexp.MALL_CHECKOUT.test(pathname) ||
            PathnameRegexp.ORDER_MALL_CHECKOUT.test(pathname) ||
            PathnameRegexp.MALL_BRIDGE_CHECKOUT.test(pathname),
        isEasyBookCheckout: () =>
            PathnameRegexp.FLEA_MARKET_CHECKOUT.test(pathname) ||
            PathnameRegexp.EASY_BOOKING_CHECKOUT.test(pathname) ||
            PathnameRegexp.ORDER_EASY_BOOKING_CHECKOUT.test(pathname) ||
            PathnameRegexp.EASY_BOOKING_BRIDGE_CHECKOUT.test(pathname),
        isSellerCheckout: () => PathnameRegexp.SELLER_CHECKOUT.test(pathname),
        isFamilyPayCheckout: () => PathnameRegexp.FAMILY_PAY_CHECKOUT.test(pathname),
        isOrderResult: () => PathnameRegexp.ORDER_RESULT.test(pathname),
        isMallResult: () => PathnameRegexp.MALL_RESULT.test(pathname),
        isEasyBookResult: () => PathnameRegexp.EASY_BOOKING_RESULT.test(pathname),
        isLocalPayResult: () => PathnameRegexp.LOCAL_PAY_RESULT.test(pathname),
        isFleaMarketResult: () => PathnameRegexp.FLEA_MARKET_RESULT.test(pathname),
        isBlogResult: () => PathnameRegexp.BLOG_RESULT.test(pathname),
        isSellerResult: () =>
            PathnameRegexp.SELLER_RESULT.test(pathname) || PathnameRegexp.ORDER_SHEET_SELLER_RESULT.test(pathname),
        isOrderSheetSellerResult: () => PathnameRegexp.ORDER_SHEET_SELLER_RESULT.test(pathname),
        isOrderSheetApp: () => PathnameRegexp.ORDER_SHEET_APP.test(pathname),
        isOrderDetail: () => PathnameRegexp.ORDER_DETAIL.test(pathname),
        isPurchaseConfirmation: () => PathnameRegexp.PURCHASE_CONFIRMATION.test(pathname),
        isDiconAuto: () => pathname.startsWith(PAYMENT_METHODS_ROUTES.DICON_AUTO.MAIN),
        isPartnersAuto: () => pathname.startsWith(PAYMENT_ROUTES.PARTNERS_AUTO.MAIN),
        isPaymentDetail,
        isReceivePoint: () => PathnameRegexp.RECEIVE_POINT.test(pathname),
        isSubscription,
        isSubscriptionManagement,
        isShoppingSubscription,
        isNaverFinancialAuto: () => pathname.startsWith(PAYMENT_ROUTES.NAVER_FINANCIAL_AUTO.MAIN),
        isNaverFinancialPayment: () => pathname.startsWith(PAYMENT_ROUTES.NAVER_FINANCIAL_PAYMENT.MAIN),
        isCoupon: () => pathname.startsWith(ROUTER_ORDER_COUPON.ROOT.getPath()),
        isDeliveryManagerChange: () => PathnameRegexp.DELIVERY_MANAGER_CHANGE.test(pathname),
        isPaymentMethodManagement: () => PathnameRegexp.PAYMENT_METHOD_MANAGEMENT.test(pathname),
        isSaleHistory: () => PathnameRegexp.SALE_HISTORY.test(pathname),
        isSaleHistoryDetail: () => PathnameRegexp.SALE_HISTORY_DETAIL.test(pathname),
        isSaleSettlement: () => PathnameRegexp.SALE_SETTLEMENT.test(pathname),
        isSaleSettlementDetail: () => PathnameRegexp.SALE_SETTLEMENT_DETAIL.test(pathname),
        isNpayBizPaymentCheckout,
        isInvoice,
    }
}

/**
 * 경로명을 기반으로 페이지 세트(PageSet)를 결정합니다.
 * @param location - 분석할 경로명 (기본값: window.location.pathname)
 * @returns 페이지 세트 상수 또는 null
 */
export const getPageSetByPathname = (location: string = window.location.pathname) => {
    const {
        isOrderSheetApp,
        isMallCheckout,
        isEasyBookCheckout,
        isSellerCheckout,
        isFamilyPayCheckout,
        isMallResult,
        isEasyBookResult,
        isSellerResult,
        isOrderDetail,
        isPurchaseConfirmation,
        isDiconAuto,
        isPartnersAuto,
        isDiconBuyingPathname,
        isPartnersPathname,
        isPaymentDetail,
        isReceivePoint,
        isShoppingSubscription,
        isNaverFinancialAuto,
        isNaverFinancialPayment,
        isPaymentMethodManagement,
        isSaleHistory,
        isSaleHistoryDetail,
        isSaleSettlement,
        isSaleSettlementDetail,
        isNpayBizPaymentCheckout,
        isInvoice,
    } = getPathNameInfo(location)

    if (isOrderSheetApp()) {
        if (isMallCheckout()) {
            return PageSet.MALL_CHECKOUT
        } else if (isEasyBookCheckout()) {
            return PageSet.EASY_BOOK_CHECKOUT
        } else if (isSellerCheckout()) {
            return PageSet.SELLER_CHECKOUT
        } else if (isFamilyPayCheckout()) {
            return PageSet.FAMILY_PAY_CHECKOUT
        } else if (isMallResult()) {
            return PageSet.MALL_RESULT
        } else if (isEasyBookResult()) {
            return PageSet.EASY_BOOK_RESULT
        } else if (isSellerResult()) {
            return PageSet.SELLER_RESULT
        } else if (isOrderDetail()) {
            return PageSet.ORDER_DETAIL
        } else if (isPurchaseConfirmation()) {
            return PageSet.PURCHASE_CONFIRMATION
        } else if (isShoppingSubscription()) {
            return PageSet.SHOPPING_SUBSCRIPTION
        }
    }

    if (isDiconAuto()) {
        return PageSet.DICON_AUTO
    }

    if (isPartnersAuto()) {
        return PageSet.PARTNERS_AUTO
    }

    if (isDiconBuyingPathname()) {
        return PageSet.DICON_PAYMENT
    }

    if (isPartnersPathname()) {
        return PageSet.PARTNERS_PAYMENT
    }

    if (isPaymentDetail()) {
        return PageSet.PAYMENT_DETAIL
    }

    if (isReceivePoint()) {
        return PageSet.RECEIVE_POINT
    }

    if (isNaverFinancialAuto()) {
        return PageSet.NAVER_FINANCIAL_AUTO
    }

    if (isNaverFinancialPayment()) {
        return PageSet.NAVER_FINANCIAL_PAYMENT
    }

    if (isPaymentMethodManagement()) {
        return PageSet.PAYMENT_METHOD_MANAGEMENT
    }

    if (isSaleHistory()) {
        return PageSet.SALE_HISTORY
    }

    if (isSaleHistoryDetail()) {
        return PageSet.SALE_HISTORY_DETAIL
    }

    if (isSaleSettlement()) {
        return PageSet.SALE_SETTLEMENT
    }

    if (isSaleSettlementDetail()) {
        return PageSet.SALE_SETTLEMENT_DETAIL
    }

    if (isNpayBizPaymentCheckout()) {
        return PageSet.NPAY_BIZ_CHECKOUT
    }

    if (isInvoice()) {
        return PageSet.INVOICE
    }

    return null
}

/**
 * 경로명에서 주문서 ID를 추출합니다.
 * @param pathname - 분석할 경로명 (기본값: window.location.pathname)
 * @returns 주문서 ID 문자열
 */
export const getOrderSheetId = (pathname: string = window.location.pathname) => {
    const {
        isOrderSheetCheckoutPathname,
        isOrderPathname,
        isSellerPathname,
        isDiconBuyingPathname,
        isDiconAutoPathname,
        isPartnersAutoPathname,
        isSubscription,
        isNpayBizPaymentCheckout,
    } = getPathNameInfo(pathname)

    if (isOrderSheetCheckoutPathname()) {
        return pathname.replace(PathnameRegexp.ORDER_SHEET_CHECKOUT, '').split('/')[0] ?? ''
    } else if (isOrderPathname()) {
        return pathname.replace(PathnameRegexp.ORDER, '').split('/')[0] ?? ''
    } else if (isSellerPathname()) {
        return pathname.replace(PathnameRegexp.SELLER, '').split('/')[0] ?? ''
    } else if (isDiconBuyingPathname()) {
        return pathname.replace(PathnameRegexp.DICON_BUYING, '')
    } else if (isDiconAutoPathname()) {
        return pathname.replace(PathnameRegexp.DICON_SUBSCRIBE, '')
    } else if (isPartnersAutoPathname()) {
        return pathname.replace(PathnameRegexp.PARTNERS_AUTO, '')
    } else if (isSubscription()) {
        return pathname.replace(PathnameRegexp.SUBSCRIPTION, '').split('/')[1] ?? ''
    } else if (isNpayBizPaymentCheckout()) {
        return pathname.replace(PathnameRegexp.NPAY_BIZ_PAYMENT_CHECKOUT, '').split('/')[1] ?? ''
    } else {
        return ''
    }
}

/**
 * Location 객체에서 주문 번호를 추출합니다.
 * @param location - Location 객체 (기본값: window.location)
 * @returns 주문 번호 문자열
 */
export const getOrderNo = (location: Location = window.location) => {
    const {
        isDetailPathName,
        isResultPathName,
        isDeliveryManagerPathName,
        isCoupon,
        isDeliveryManagerChange,
        isReceivePoint,
    } = getPathNameInfo(location.pathname)

    if (isDetailPathName()) {
        return location.pathname.replace(PathnameRegexp.DETAIL, '')
    } else if (isResultPathName()) {
        return location.pathname.replace(PathnameRegexp.RESULT, '')
    } else if (isDeliveryManagerChange()) {
        return location.pathname.replace(PathnameRegexp.DELIVERY_MANAGER_CHANGE, '')
    } else if (isDeliveryManagerPathName()) {
        return location.pathname.replace(PathnameRegexp.DELIVERY_MANAGER, '')
    } else if (isCoupon()) {
        const segments = location.pathname.split('/')
        return segments[segments.length - 1] ?? ''
    } else if (isReceivePoint()) {
        return location.pathname.replace(PathnameRegexp.RECEIVE_POINT, '')
    } else {
        return ''
    }
}
/**
 * Location 객체에서 시퀀스 ID를 추출합니다. 주문서 ID 또는 주문 번호를 반환합니다.
 * @param location - Location 객체 (기본값: window.location)
 * @returns 시퀀스 ID 문자열
 */
export const getSeqId = (location: Location = window.location) => {
    return getOrderSheetId(location.pathname) || getOrderNo(location)
}

/**
 * Location 객체의 쿼리 파라미터에서 언어 설정을 추출합니다.
 * @param location - Location 객체 (기본값: window.location)
 * @returns 언어 타입 ('ko' 기본값)
 */
export const getLanguage = (location: Location = window.location): LanguageType => {
    const query = qs.parse(location.search)
    return query['language'] ?? 'ko'
}

/**
 * CommonState의 초기 상태를 생성합니다.
 * @returns 초기 CommonState 객체
 */
export const getInitialState = (): CommonState => ({
    backUrl: '',
    // Try1: slice 초기화 시점에 window.location 으로 부터 channel을 추출하여 저장합니다.
    backUrlParamForCancel: null,
    closingType: null,
    closingTiming: ClosingTiming.IMMEDIATE,
    entryPoint: null,
    fallbackUrl: getFallbackUrl(window.location.href),
    initOrderSheetDone: false,
    scene: APIStatus.IDLE,
    orderPhase: null,
    orderSheetUrl: getOrderSheetUrl(),
    returnUrl: null,
    returnMethod: null,
    // Try1: slice 초기화 시점에 window.location 으로 부터 seqID를 추출하여 저장합니다.
    seqID: getSeqId(),
    pageSet: getPageSetByPathname(window.location.pathname),
    entry: getEntryFromPathname(window.location.pathname),
    channel: null,
    payNecessary: true,
    language: 'ko',
    isInternationalPayment: false,
})

/**
 * CommonState의 모의(mock) 상태 생성기를 생성합니다.
 */
export const makeMock = createStateMaker<CommonState>(getInitialState)

/**
 * 판매 채널 타입과 예약 채널 타입을 기반으로 주문 진입점을 결정합니다.
 * @param saleChannelType - 판매 채널 타입
 * @param scheduledChannelType - 예약 채널 타입 (선택)
 * @returns 주문 진입점 타입
 */
export function getSaleChannelType(saleChannelType: string, scheduledChannelType?: string): OrderEntryType {
    switch (scheduledChannelType) {
        case 'HAPPY_BEAN':
            return Entry.HAPPY_BEAN_ORDER
    }

    switch (saleChannelType) {
        case 'EASYBOOKING':
            return Entry.EASY_BOOKING
        case 'LOCALPAY':
            return Entry.LOCAL_PAY
        case 'NAVERBLOG':
            return Entry.NAVER_BLOG
        case 'FLEAMARKET':
            return Entry.FLEA_MARKET
        default:
            return saleChannelType as OrderEntryType
    }
}

// https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=120900972
// development, production 순서로 작성
export const DICON_ENTRY_SERVICE_SEQUENCE: Record<Constant<typeof DiconEntry>, [string, string]> = {
    [DiconEntry.B612]: ['10016043', '10001897'],
    [DiconEntry.CHZZK_PRIME]: ['10016103', '10001937'],
    [DiconEntry.CHZZK]: ['10015823', '10001718'],
    [DiconEntry.CLOVA_DUBBING]: ['10015843', '10001717'],
    [DiconEntry.CLOVA_NOTE]: ['10015783', '10001677'],
    [DiconEntry.CREDIT_MANAGEMENT]: ['10016283', '10002077'],
    [DiconEntry.GIFT_CARD]: ['10015723', '10001639'],
    [DiconEntry.HAPPY_BEAN]: ['10000183', '10000063'],
    [DiconEntry.KIN]: ['10015183', '10001237'],
    [DiconEntry.MY_GOLF]: ['10015923', '10001777'],
    [DiconEntry.MYBOX]: ['10012366', '10000955'],
    [DiconEntry.N_CLOUD]: ['10015443', '10001457'],
    [DiconEntry.N_STORE]: ['10010017', '10000953'],
    [DiconEntry.NAVER_BUSINESS_SCHOOL]: ['10016143', '10001979'],
    [DiconEntry.NAVER_GAME]: ['10013743', '10000998'],
    [DiconEntry.NAVER_MEMBERSHIP]: ['10015223', '10001277'],
    [DiconEntry.OGQ_MARKET]: ['10014903', '10001101'],
    [DiconEntry.PAPAGO]: ['10015924', '10001778'],
    [DiconEntry.PREMIUM_CONTENTS]: ['10015324', '10001397'],
    [DiconEntry.SEARCH_AD]: ['10015303', '10001317'],
    [DiconEntry.SERIES_EBOOK]: ['10016083', '10001917'],
    [DiconEntry.SERIES]: ['10000123', '10000083'],
    [DiconEntry.SNOW]: ['10015963', '10001797'],
    [DiconEntry.TAROTOON]: ['10016063', '10001877'],
    [DiconEntry.VIBE]: ['10006486', '10000913'],
    [DiconEntry.WEBTOON_AILABS]: ['10015903', '10001757'],
    [DiconEntry.WIKIT]: ['10016203', '10001997'],
}

/**
 * 서비스 시퀀스 번호를 기반으로 진입점(Entry)을 찾습니다.
 * @param svcSeq - 서비스 시퀀스 번호
 * @returns 진입점 타입 또는 기본값 Entry.ETC
 */
export const serviceSequenceToEntry = (svcSeq: string) => {
    const env = isLocalProfile || isAlphaProfile || isDevProfile ? 0 : 1

    return (
        (Object.entries(DICON_ENTRY_SERVICE_SEQUENCE).find(
            ([_, sequences]) => sequences[env] === svcSeq,
        )?.[0] as EntryType) ?? Entry.ETC
    )
}

/**
 * 현재 URL에서 폴백 URL을 추출합니다. 쿼리 파라미터 fallbackUrl이 있으면 사용, 없으면 referrer를 반환합니다.
 * @param currentUrl - 현재 URL
 * @returns 폴백 URL
 */
function getFallbackUrl(currentUrl: string) {
    const {query} = qs.parseUrl(currentUrl)
    if (query?.fallbackUrl) {
        return query.fallbackUrl
    }

    return window.document.referrer
}

/**
 * 진입점(Entry)을 기반으로 서비스 시퀀스 번호를 반환합니다.
 * @param entry - 진입점 타입
 * @returns 서비스 시퀀스 번호 또는 빈 문자열
 */
export const entryToServiceSequence = (entry: Maybe<EntryType>) => {
    const env = isLocalProfile || isAlphaProfile || isDevProfile ? 0 : 1

    if (entry && entry in DICON_ENTRY_SERVICE_SEQUENCE) {
        return DICON_ENTRY_SERVICE_SEQUENCE[entry as keyof typeof DICON_ENTRY_SERVICE_SEQUENCE][env] ?? ''
    }

    return ''
}

// development, production 순서로 작성
const PARTENRS_ENTRY_MERCHANT_NO = {
    [PartnersEntry.WETAX]: [
        [1200191955, 300049910],
        [200191955, 200191960],
    ],
}

/**
 * 가맹점 번호를 기반으로 진입점(Entry)을 찾습니다.
 * @param merchantNo - 가맹점 번호
 * @returns 진입점 타입 또는 기본값 Entry.ETC
 */
export const merchantNoToEntry = (merchantNo: number) => {
    const env = isLocalProfile || isAlphaProfile || isDevProfile ? 0 : 1

    return (
        (Object.entries(PARTENRS_ENTRY_MERCHANT_NO).find(([_, merchantNos]) =>
            merchantNos[env]?.includes(merchantNo),
        )?.[0] as EntryType) ?? Entry.ETC
    )
}
